import { useCallback, useEffect, useRef, useState } from 'react'
import {
  appendDictation,
  getSpeechRecognitionCtor,
  speechErrorMessage,
  speechRecognitionSupported,
  type SpeechRecognitionLike,
} from './speechDictation'

type Options = {
  /** When false, any active session is stopped (e.g. panel closed / AI gated off). */
  active: boolean
  maxLength: number
  onFinal: (nextMessage: string) => void
  onError: (message: string) => void
  lang?: string
}

/**
 * Toggle browser speech recognition into the chat composer.
 * Final results append to the current message; interim text is exposed for display.
 */
export function useSpeechDictation({
  active,
  maxLength,
  onFinal,
  onError,
  lang = 'en-IN',
}: Options) {
  const [listening, setListening] = useState(false)
  const [interim, setInterim] = useState('')
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null)
  const baseRef = useRef('')
  const finalsRef = useRef('')
  const onFinalRef = useRef(onFinal)
  const onErrorRef = useRef(onError)
  const maxLengthRef = useRef(maxLength)
  const supported = speechRecognitionSupported()

  useEffect(() => {
    onFinalRef.current = onFinal
  }, [onFinal])
  useEffect(() => {
    onErrorRef.current = onError
  }, [onError])
  useEffect(() => {
    maxLengthRef.current = maxLength
  }, [maxLength])

  const stop = useCallback(() => {
    const rec = recognitionRef.current
    recognitionRef.current = null
    if (rec) {
      rec.onresult = null
      rec.onerror = null
      rec.onend = null
      try {
        rec.stop()
      } catch {
        /* already stopped */
      }
    }
    setListening(false)
    setInterim('')
    finalsRef.current = ''
  }, [])

  useEffect(() => {
    if (!active) stop()
  }, [active, stop])

  useEffect(() => () => stop(), [stop])

  const start = useCallback(
    (currentMessage: string) => {
      if (!supported) {
        onErrorRef.current(
          'Dictation needs a browser with speech recognition (Chrome or Edge on desktop).',
        )
        return
      }
      const Ctor = getSpeechRecognitionCtor()
      if (!Ctor) return

      stop()
      baseRef.current = currentMessage
      finalsRef.current = ''

      const recognition = new Ctor()
      recognition.continuous = true
      recognition.interimResults = true
      recognition.lang = lang

      recognition.onresult = (event) => {
        let interimChunk = ''
        let finalChunk = ''
        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          const row = event.results[i]
          if (!row) continue
          const text = row[0]?.transcript ?? ''
          if (row.isFinal) finalChunk += text
          else interimChunk += text
        }
        if (finalChunk) {
          finalsRef.current = appendDictation(finalsRef.current, finalChunk, maxLengthRef.current)
          const next = appendDictation(baseRef.current, finalsRef.current, maxLengthRef.current)
          onFinalRef.current(next)
        }
        setInterim(interimChunk.trim())
      }

      recognition.onerror = (event) => {
        const msg = speechErrorMessage(event.error)
        if (msg) onErrorRef.current(msg)
        stop()
      }

      recognition.onend = () => {
        // Continuous mode can end after a pause; keep UI honest.
        if (recognitionRef.current === recognition) {
          recognitionRef.current = null
          setListening(false)
          setInterim('')
        }
      }

      recognitionRef.current = recognition
      try {
        recognition.start()
        setListening(true)
      } catch {
        onErrorRef.current('Could not start the microphone. Try again.')
        stop()
      }
    },
    [lang, stop, supported],
  )

  const toggle = useCallback(
    (currentMessage: string) => {
      if (listening) stop()
      else start(currentMessage)
    },
    [listening, start, stop],
  )

  const previewMessage = interim
    ? appendDictation(
        appendDictation(baseRef.current, finalsRef.current, maxLength),
        interim,
        maxLength,
      )
    : null

  return {
    supported,
    listening,
    interim,
    previewMessage,
    toggle,
    stop,
  }
}
